import classNames from 'classnames/bind';
import styles from './SettingsMain.module.css';
import { Link } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';

import { apiSlice, useLogoutMutation } from '../../../services/api';
import { persistor } from '../../../services/store';
import { selectLoginType, selectUser } from '../../../features/authSlice';

import { Notification } from '../../../types/common';

import {
  ACCOUNT_INFO,
  FAQ,
  FEEDBACK,
  LOGOUT,
  MEMBERSHIP,
  NOTICE,
  PREFERENCES,
  PRIVACY_POLICY,
  SERVICE_INFO,
  SERVICE_NAME,
  SETTINGS,
  SHARED_LINK_NOTIFICATION,
  STORAGE,
  STUDENT_REGISTRATION_REQUEST,
  TERMS,
} from '../../../constants';
import {
  deleteAllTokens,
  getAccessToken,
  setLocalStorage,
} from '../../../utils/storage';
import {
  isMobile,
  VITE_APP_VERSION,
  VITE_SERVICE_TYPE,
} from '../../../utils/util';

import { selectNotifications } from '../../../features/mainSlice';

export default function SettingsMain() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUser);
  const loginType = useAppSelector(selectLoginType);
  const notifications = useAppSelector(selectNotifications);
  const [logout] = useLogoutMutation();

  const preferences = [
    { name: ACCOUNT_INFO, link: loginType === 'naver' ? 'profile' : 'account' },
    { name: MEMBERSHIP, link: 'membership' },
    { name: STORAGE, link: 'storage' },
    { name: SHARED_LINK_NOTIFICATION, link: 'sharedLink' },
    {
      name: STUDENT_REGISTRATION_REQUEST,
      link: 'student-registration-request',
    },
  ];
  const services = [
    ...(VITE_SERVICE_TYPE === 'B2C'
      ? [{ name: NOTICE, link: 'https://selpub.com/notice' }]
      : []),
    { name: FAQ, link: 'faq' },
    { name: FEEDBACK, link: 'feedback' },
    { name: TERMS, link: 'terms' },
    { name: PRIVACY_POLICY, link: 'privacy-policy' },
  ];

  const hasStorageNoti = notifications?.some(
    (list: Notification) => list.code === '100'
  );
  const hasNewSharedLink = notifications?.some(
    (list: Notification) => list.code === '200'
  );
  const hasStudentRegReq = notifications?.some(
    (list: Notification) => list.code === '300'
  );

  const logoutHandler = async () => {
    const accessToken = getAccessToken();
    setLocalStorage('loggedIn', 0);
    try {
      await logout({ accessToken }).unwrap();
      dispatch(apiSlice.util.resetApiState());
      deleteAllTokens();
      await persistor.purge();
    } catch (error) {
      console.log('Fail to Logout', error);
    }
  };

  if (!user) return null;

  return (
    <>
      {!isMobile && <h2>{SETTINGS}</h2>}
      <div className={cx('login_area')}>
        {!isMobile && (
          <div className={cx('items')}>
            <span className={cx('svg_login')}>
              <span>{user.email[0].toUpperCase()}</span>
            </span>
          </div>
        )}
        <div className={cx('items')}>
          <span className={cx('user_id', 'text_clip')}>{user.alias}</span>
          <span className={cx('user_email', 'text_clip')}>{user.email}</span>
        </div>
        <div className={cx('items')}>
          <Link to="/login" className={cx('abtn')} onClick={logoutHandler}>
            <span>{LOGOUT}</span>
          </Link>
        </div>
      </div>

      <div className={cx('sub_area')}>
        <p>{PREFERENCES}</p>
        <ul>
          {preferences.map(({ name, link }) => (
            <Link key={name} to={link}>
              <li className={cx('items')}>
                <span
                  className={cx({
                    notice:
                      (name === STORAGE && hasStorageNoti) ||
                      (name === SHARED_LINK_NOTIFICATION && hasNewSharedLink) ||
                      (link === 'student-registration-request' &&
                        hasStudentRegReq),
                  })}
                >
                  {name}
                </span>
              </li>
            </Link>
          ))}
        </ul>
      </div>

      <div className={cx('sub_area')}>
        <p>{SERVICE_INFO}</p>
        <ul>
          {services.map(({ name, link }) => (
            <Link key={name} to={link}>
              <li className={cx('items')}>
                <span>{name}</span>
              </li>
            </Link>
          ))}
        </ul>
      </div>

      <div className={cx('version')}>
        {SERVICE_NAME} {VITE_APP_VERSION}
      </div>
    </>
  );
}
